import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { inspectionApi } from '../features/inspections/inspectionAPI';
import type { InspectionAssetsResponse } from '../types/common/asset';
import type { ApiError } from '../types/api/network';

type UseInspectionAssetsParams = {
  inspectionId?: string;
  enabled?: boolean;
};

interface UseInspectionAssetsReturn {
  assets: InspectionAssetsResponse | null;
  loadingAssets: boolean;
  assetsLoaded: boolean;
  error: ApiError | Error | null;
  fetchAssets: (inspectionId?: string) => Promise<InspectionAssetsResponse | void>;
  resetAssets: () => void;
}

const useInspectionAssets = (params: UseInspectionAssetsParams = {}): UseInspectionAssetsReturn => {
  const { inspectionId = '', enabled = false } = params;
  const dispatch = useDispatch();

  const {
    data: queriedAssets,
    isLoading,
    isFetching,
    isSuccess,
    error: queryError,
  } = inspectionApi.useGetInspectionAssetsQuery(inspectionId, {
    skip: !inspectionId || !enabled,
  });

  const { data: cachedAssets } = inspectionApi.endpoints.getInspectionAssets.useQueryState(inspectionId, {
    skip: !inspectionId,
  });

  const [triggerFetchAssets, { isFetching: isManualFetching, error: lazyError }] =
    inspectionApi.useLazyGetInspectionAssetsQuery();

  const assets = (enabled ? queriedAssets : cachedAssets) ?? null;
  const assetsLoaded = enabled ? isSuccess || queriedAssets !== undefined : cachedAssets !== undefined;
  const error = ((enabled ? queryError : undefined) ?? lazyError ?? null) as ApiError | Error | null;

  const fetchAssets = useCallback(
    async (nextInspectionId?: string) => {
      const targetInspectionId = nextInspectionId || inspectionId;
      if (!targetInspectionId) {
        return;
      }

      return await triggerFetchAssets(targetInspectionId).unwrap();
    },
    [inspectionId, triggerFetchAssets]
  );

  const resetAssets = useCallback(() => {
    if (!inspectionId) {
      return;
    }

    dispatch(inspectionApi.util.invalidateTags([{ type: 'InspectionAssets', id: inspectionId }]));
  }, [dispatch, inspectionId]);

  return {
    assets,
    loadingAssets: (enabled && (isLoading || isFetching)) || isManualFetching,
    assetsLoaded,
    error,
    fetchAssets,
    resetAssets,
  };
};

export default useInspectionAssets;
